import React from 'react';
import { Check, Sun, Moon, Palette } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Full theme picker shown inside Profile → Appearance.
 * Hidden entirely for users who can't toggle (customers, VIPs).
 */
const AppearancePanel = () => {
  const { theme, effectiveTheme, canToggle, setTheme, themes, forceDark } = useTheme();

  if (!canToggle) {
    return (
      <div className="bg-[#141414] border border-[#262626] rounded-md p-6 max-w-2xl">
        <p className="text-[#A3A3A3] text-sm">
          Appearance customization isn&apos;t available for your account type. The portal stays in
          its branded dark theme for a consistent experience.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-5 max-w-3xl">
      <div>
        <h3 className="font-['Outfit'] text-lg text-white flex items-center gap-2">
          <Palette className="w-5 h-5 text-[#D4AF37]" /> Theme
        </h3>
        <p className="text-[#A3A3A3] text-sm mt-1">
          Choose how NexFlow looks for you. Your preference is saved to your account and follows
          you across devices. Customer and VIP portals always stay in the default dark theme.
        </p>
      </div>

      {forceDark && (
        <div
          data-testid="appearance-forced-dark-note"
          className="text-xs text-[#A3A3A3] bg-[#0A0A0A] border border-[#262626] rounded-md px-3 py-2"
        >
          Note: this page is rendered in dark mode regardless of your preference (public/portal
          surface). Internal pages will use your chosen theme.
        </div>
      )}

      <div data-testid="appearance-theme-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {themes.map((t) => {
          const selected = theme === t.id;
          const Icon = t.isDark ? Moon : Sun;
          return (
            <button
              key={t.id}
              type="button"
              data-testid={`appearance-theme-${t.id}`}
              onClick={() => setTheme(t.id)}
              className={`text-left rounded-lg border p-4 transition-all ${
                selected
                  ? 'border-[#D4AF37] bg-[#D4AF37]/8'
                  : 'border-[#262626] hover:border-[#525252] bg-[#0A0A0A]'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-5 h-5 rounded border border-[#404040]"
                    style={{ backgroundColor: t.bgHex }}
                    aria-hidden
                  />
                  <span
                    className="inline-block w-5 h-5 rounded border border-[#404040]"
                    style={{ backgroundColor: t.accentHex }}
                    aria-hidden
                  />
                </div>
                <Icon className="w-4 h-4 text-[#A3A3A3]" />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <p className="text-white font-medium">{t.label}</p>
                {selected && (
                  <span className="inline-flex items-center gap-1 text-[#D4AF37] text-xs">
                    <Check className="w-3 h-3" /> Current
                  </span>
                )}
              </div>
              <p className="text-[#A3A3A3] text-xs mt-1 leading-relaxed">{t.description}</p>
            </button>
          );
        })}
      </div>

      <div className="text-xs text-[#525252]">
        Active right now: <span className="text-white">{effectiveTheme}</span>
        {effectiveTheme !== theme && (
          <> · your saved preference is <span className="text-white">{theme}</span></>
        )}
      </div>
    </div>
  );
};

export default AppearancePanel;
